import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Reusable pagination footer.
 *
 * Props:
 *  - total          (number) total items
 *  - page           (number, 1-indexed) current page
 *  - pageSize       (number) items per page
 *  - onPageChange   (fn) (newPage) => void
 *  - onPageSizeChange (fn) (newSize) => void
 *  - testId         (string) optional, used for data-testid prefix
 *  - itemLabel      (string) singular noun for the items, e.g. "job", "staff member"
 */
export default function Pagination({
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  testId = 'pagination',
  itemLabel = 'item',
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);

  const goto = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== safePage) onPageChange?.(next);
  };

  // Build a compact page number list with ellipses
  const pages = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, 2, totalPages - 1, totalPages, safePage - 1, safePage, safePage + 1]);
    const arr = Array.from(set).filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of arr) {
      if (n - prev > 1) out.push('…');
      out.push(n);
      prev = n;
    }
    return out;
  })();

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-2 py-3 mt-4"
      data-testid={testId}
    >
      <div className="text-xs sm:text-sm" style={{ color: '#A0AEC0' }}>
        {total === 0 ? (
          <span>No {itemLabel}s</span>
        ) : (
          <>
            Showing <span className="font-semibold text-white">{start}</span>
            {start !== end && <> – <span className="font-semibold text-white">{end}</span></>}
            {' '}of <span className="font-semibold text-white">{total}</span> {itemLabel}{total !== 1 ? 's' : ''}
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs sm:text-sm" style={{ color: '#A0AEC0' }} htmlFor={`${testId}-size`}>
            Show
          </label>
          <select
            id={`${testId}-size`}
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            className="text-xs sm:text-sm rounded px-2 py-1 outline-none cursor-pointer"
            style={{ background: '#1A2433', color: '#FFFFFF', border: '1px solid #2D3748' }}
            data-testid={`${testId}-size-select`}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="text-xs sm:text-sm hidden sm:inline" style={{ color: '#A0AEC0' }}>per page</span>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1" data-testid={`${testId}-controls`}>
            <button
              onClick={() => goto(safePage - 1)}
              disabled={safePage <= 1}
              className="p-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/5"
              style={{ color: '#A0AEC0' }}
              aria-label="Previous page"
              data-testid={`${testId}-prev`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {pages.map((p, i) =>
              p === '…' ? (
                <span key={`e-${i}`} className="px-1 text-xs" style={{ color: '#64748B' }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => goto(p)}
                  className="min-w-[28px] h-7 rounded text-xs font-semibold"
                  style={{
                    background: p === safePage ? '#1E88E5' : 'transparent',
                    color: p === safePage ? '#FFFFFF' : '#A0AEC0',
                    border: '1px solid ' + (p === safePage ? '#1E88E5' : 'transparent'),
                  }}
                  data-testid={`${testId}-page-${p}`}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => goto(safePage + 1)}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/5"
              style={{ color: '#A0AEC0' }}
              aria-label="Next page"
              data-testid={`${testId}-next`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
